"use client";

import { useLayoutEffect } from "react";
import {
  BrandMultiLineX,
  BrandWordmark,
} from "@/components/icons/SiteMarks";
import type { StatusCopy } from "@/lib/status-copy";
import {
  acquireSubpageDocument,
  releaseSubpageDocument,
} from "@/components/pages/subpage-document";
import styles from "./status-page.module.css";

type StatusPageProps = {
  copy: StatusCopy;
  /** Optional recover control for error boundaries. */
  onReset?: () => void;
};

/**
 * Branded 404 / error surface — document flow like ADR-020 subpages.
 * Adds `html.subpage` / `body.subpage` so spine chrome (FakeThumbs, jump) stays off.
 */
export function StatusPage({ copy, onReset }: StatusPageProps) {
  useLayoutEffect(() => {
    acquireSubpageDocument();
    return () => releaseSubpageDocument();
  }, []);

  return (
    <div className={styles.root} data-status-page={copy.locale}>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <a href={copy.homeHref} className={styles.logo} aria-label="Xattir">
            <BrandWordmark className={styles.logoMark} />
          </a>
        </div>
      </div>

      <main className={styles.main}>
        <BrandMultiLineX className={styles.mark} />
        <span className={styles.eyebrow}>{copy.code}</span>
        <h1 className={styles.headline}>{copy.title}</h1>
        <p className={styles.body}>{copy.body}</p>

        <div className={styles.actions}>
          {onReset && copy.resetLabel ? (
            <button
              type="button"
              className={styles.reset}
              onClick={onReset}
            >
              <strong className={styles.label}>{copy.resetLabel}</strong>
            </button>
          ) : null}
          <a href={copy.homeHref} className={styles.primary}>
            <strong className={styles.label}>{copy.homeLabel}</strong>
          </a>
          <a href={copy.projectHref} className={styles.secondary}>
            <strong className={styles.label}>{copy.projectLabel}</strong>
          </a>
        </div>

        <a
          href={copy.contactHref}
          className={styles.contact}
          dir="ltr"
        >
          {copy.contactLabel}
        </a>
      </main>
    </div>
  );
}
